<script lang="ts" setup>
  import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue';
  import { useScrollDetector, useBreakpoints, useCustomerOrderAbility, viewItemListEvent, searchTermEvent, useGoogleTagManager, selectItemEvent } from '@storefront/storefront';
  import { useHttpClient, useViewQuery, useShared, useRoute } from '@othercommerce/foundation';
  import type { ProductListingResource } from '@storefront/resources';

  defineProps({
    placeholder: { type: String, required: true },
  });

  const emit = defineEmits(['update:resultsOpen']);

  const results = ref();
  const search = ref('');
  const barcodeScannerOpen = ref(false);
  const isFocused = ref(false);
  const loading = ref(true);
  const searchHtml = ref();
  const activeIndex = ref(-1);

  const route = useRoute();
  const http = useHttpClient();
  const query = useViewQuery();
  const breakpoints = useBreakpoints();
  const canCustomerOrder = useCustomerOrderAbility();
  const gtm = useGoogleTagManager();
  const shared = useShared();

  const activeOptionId = computed(() => {
    if (activeIndex.value >= 0 && results.value?.data?.[activeIndex.value]) {
      return `search-option-${results.value.data[activeIndex.value].id}`;
    }
    return undefined;
  });

  const resultsOpen = computed(() => {
    return isFocused.value && search?.value.length > 0;
  });

  let searchTimeout: number;
  let analyticsTimeout: number;

  watch(search, (value) => {
    loading.value = true;
    clearTimeout(searchTimeout);
    clearTimeout(analyticsTimeout);

    searchTimeout = window.setTimeout(() => {
      http.get(
        route('api.v1.web.product.search', { query: value }),
      ).then((res) => {
        results.value = res;
        loading.value = false;
      });
    }, 250);

    analyticsTimeout = window.setTimeout(() => {
      if (value.length > 0 && query.value?.search !== value) {
        gtm.push(searchTermEvent(value));
      }
    }, 2000);
  });

  watch(() => resultsOpen.value, (value) => {
    emit('update:resultsOpen', value);
  });

  useScrollDetector({
    threshold: 8,
    callback: ({ down }) => {
      if (down && !breakpoints.value.lg) {
        search.value = '';
      }
    },
  });

  function target(product: any) {
    return route('web.product.show', { slug: product.slug });
  }

  function onClickOutside(event: MouseEvent) {
    if (searchHtml.value && !searchHtml.value.contains(event.target as Node)) {
      isFocused.value = false;
    }
  }

  onMounted(() => {
    document.addEventListener('click', onClickOutside);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', onClickOutside);
  });

  function onFocus() {
    isFocused.value = true;
  }

  function onFocusOut() {
    isFocused.value = false;
  }

  function onRedirect() {
    http.get(route('web.product.index', { search: search.value }));
  }

  function onSearchClick() {
    onFocusOut();
    onRedirect();
  }

  function onSearchEnter(e: any) {
    e.target.blur();
    onSearchClick();
  }

  function moveDown() {
    if (!results.value?.data?.length) {
      return;
    }
    activeIndex.value = (activeIndex.value + 1) % results.value.data.length;
  }

  function moveUp() {
    if (!results.value?.data?.length) {
      return;
    }
    activeIndex.value = (activeIndex.value - 1 + results.value.data.length) % results.value.data.length;
  }

  function handleScan(value: string) {
    search.value = value;
    searchHtml.value.querySelector('#search-input').focus();
  }

  function onProductClick(product: ProductListingResource) {
    onFocusOut();
    gtm.push(selectItemEvent(product, shared.value.activeCurrencyCode));
  }

  watch(() => query.value?.search, (value) => {
    if (value) {
      search.value = value;
    } else {
      search.value = '';
    }
  });

  watch(
    [
      () => results.value?.data,
      () => isFocused.value,
    ],
    ([products, focused]) => {
      if (!focused) return;
      if (!products?.length) return;

      gtm.push(viewItemListEvent(
        products as ProductListingResource[],
        shared.value.activeCurrencyCode,
      ));
    },
    { immediate: false },
  );
</script>
<template>
  <div ref="searchHtml" :class="{ 'search--open': isFocused && search?.length > 0 }" class="search">
    <transition name="fade">
      <div v-if="isFocused && search?.length > 0" class="search__backdrop" @click.stop="onFocusOut" />
    </transition>
    <div class="search__content">
      <input
        id="search-input"
        v-model="search"
        :aria-activedescendant="activeOptionId"
        :aria-expanded="isFocused && search?.length > 0"
        :placeholder="placeholder"
        aria-autocomplete="list"
        aria-controls="search-result-wrapper"
        aria-haspopup="listbox"
        class="search__input"
        name="search"
        role="combobox"
        type="search"
        @focus="onFocus"
        @keydown.enter="onSearchEnter($event)"
        @keydown.down.prevent="moveDown"
        @keydown.up.prevent="moveUp"
      />
      <label class="search__input-label" for="search-input">{{ placeholder }}</label>
      <div class="search__icons">
        <transition name="fade">
          <SfButton v-if="search?.length > 0" :aria-label="$t('SHOP.COMMON.ARIA.RESET_SEARCH')" class="search__action-btn" square transparent @click="search = ''">
            <template #after>
              <SfIconClean aria-hidden="true" />
            </template>
          </SfButton>
        </transition>
        <SfButton :aria-label="$t('SHOP.COMMON.ARIA.SCAN_PRODUCT_BARCODE')" class="search__action-btn" square transparent @click="barcodeScannerOpen = true">
          <template #after>
            <SfIconBarcode aria-hidden="true" />
          </template>
        </SfButton>
        <SfButton :aria-label="$t('SHOP.COMMON.ARIA.SEARCH')" :disabled="search?.length <= 0" class="search__action-btn" square transparent @click="onSearchClick">
          <template #after>
            <SfIconSearch aria-hidden="true" />
          </template>
        </SfButton>
      </div>
    </div>
    <transition name="fade">
      <div v-if="isFocused && search?.length > 0" :aria-busy="loading" class="search__results-wrapper" @focusout="onFocusOut" @wheel.prevent @touchmove.prevent>
        <template v-if="!loading">
          <div v-if="results?.data?.length > 0" id="search-result-wrapper" class="search__results" role="listbox" @focusout.stop="void 0">
            <RouterLink
              v-for="(product, index) in results.data"
              :id="`search-option-${product.id}`"
              :key="product.id"
              :aria-selected="activeIndex ===  index"
              :class="{'active': activeIndex ===  index}"
              :href="target(product)"
              class="search__result-item"
              placeholder=" "
              role="option"
              @keydown.enter="onProductClick(product)"
            >
              <div class="search__product" @click="onProductClick(product)">
                <div class="search__product-left">
                  <MediaPicture :photo="product.photo" class="search__product-img" sizes="60x60" />
                  <span class="search__product-title">{{ product.title }}</span>
                </div>
                <SfProductPrice v-if="canCustomerOrder" :price="product" current />
              </div>
            </RouterLink>
          </div>
          <SfDataPlaceholder v-else aria-live="polite" role="status">{{ $t('SHOP.COMMON.MESSAGE.NO_SEARCH_RESULTS') }}</SfDataPlaceholder>
          <div class="search__buttons">
            <!--button propagates focus out by default, we want to block focus out on 1st button, otherwise it will close the modal automatically-->
            <SfButton class="search__button" primary @click="onSearchClick" @focusout.stop>{{ $t('SHOP.COMMON.ACTION.SHOW_ALL_SEARCH_RESULTS') }}</SfButton>
            <SfButton class="search__button" @click="onFocusOut">{{ $t('SHOP.COMMON.ACTION.CANCEL') }}</SfButton>
          </div>
        </template>
        <SfSpinner v-else class="search__spinner" />
      </div>
    </transition>
    <BarcodeScanner v-model:open="barcodeScannerOpen" @scanned="handleScan" />
  </div>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-search-padding: 0.875rem 7rem 0.75rem 1rem;

    --sf-search-border-style: var(--sf-input-border-style);
    --sf-search-border-bottom-width: 1px;
    --sf-search-border-top-width: 1px;
    --sf-search-border-right-width: 1px;
    --sf-search-border-left-width: 1px;
    --sf-search-border-color: var(--secondary-border);
    --sf-search-border-color-active: var(--primary-color);
    --sf-search-border-radius: var(--border-radius);

    --sf-search-background-color: var(--color-white);

    --sf-search-label-color: var(--sf-input-label-color);

    --sf-search-icons-border-left-width: 0;
    --sf-search-icons-border-right-width: 0;
    --sf-search-icons-border-bottom-width: 0;
    --sf-search-icons-border-top-width: 0;

    --sf-search-icons-padding: 0 0.5rem 0 0.5rem;
    --sf-search-icons-background: transparent;
    --sf-search-icons-color: var(--color-default);
    --sf-search-icons-color-hover: var(--primary-color);

    --sf-search-results-wrapper-background: white;
    --sf-search-results-wrapper-padding: 3.5rem 1rem 1rem 1rem;

    --sf-search-results-gap: 1rem;
    --sf-search-results-padding: 1.5rem 0 0;

    --sf-search-title-font-size: 0.875rem;

    --sf-search-spinner-padding: 1rem 0 0 0;

    --sf-search-product-left-gap: 0.5rem;

    --sf-search-buttons-margin: 1rem 0 0 0;
    --sf-search-buttons-gap: 0.5rem;

    --sf-serach-product-gap: 0.5rem;
  }

  .search {
    position: relative;
    width: 100%;

    &--open {
      @media (max-width: 992px) {
        position: absolute;
        padding: 0 1rem;
        left: 0;
        right: 0;
      }
    }

    &__backdrop {
      position: fixed;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background: var(--backdrop-default);
      z-index: 9999;

      @media (max-width: 992px) {
        background: var(--sf-search-results-wrapper-background);
      }
    }

    &__content {
      width: 100%;
      position: relative;
      z-index: 99999;
    }

    &__result-item {
      padding-right: 0.375rem;
      border-radius: var(--border-radius);

      &.active {
        background: rgba(0, 0, 0, 0.1);
      }
    }

    &__icons {
      position: absolute;
      right: 0;
      top: 0;
      display: flex;
      align-items: center;
      padding: var(--sf-search-icons-padding);
      background: var(--sf-search-icons-background);
      height: 100%;
      border-bottom-right-radius: var(--sf-search-border-radius);
      border-top-right-radius: var(--sf-search-border-radius);
      border-top: 1px var(--sf-search-border-style) var(--sf-search-icons-background);
      border-right: 1px var(--sf-search-border-style) var(--sf-search-icons-background);
      border-bottom: 1px var(--sf-search-border-style) var(--sf-search-icons-background);
      border-left: 1px var(--sf-search-border-style) var(--sf-search-border-color);
    }

    &__action-btn {
      --sf-btn-transparent-background-disabled: transparent;
      --sf-btn-transparent-border-disabled: transparent;

      svg {
        transition: var(--primary-transition);
        color: var(--sf-search-icons-color);
      }

      @media(hover: hover) {
        &:hover {
          svg {
            color: var(--sf-search-icons-color-hover);
          }
        }
      }
    }

    &__input-label {
      position: absolute;
      top: 50%;
      left: 1rem;
      transform: translateY(-50%);
      font-size: 0.75rem;
      color: var(--sf-search-label-color);
      transition-timing-function: var(--transition-primary-timing-function);
      transition-property: color, transition, top, left;
      transition-duration: var(--transition-primary-duration);
      pointer-events: none;
    }

    &__input {
      width: 100%;
      padding: var(--sf-search-padding);
      background-color: var(--sf-search-background-color);
      border-style: var(--sf-search-border-style);
      border-bottom-width: var(--sf-search-border-bottom-width);
      border-top-width: var(--sf-search-border-top-width);
      border-right-width: var(--sf-search-border-right-width);
      border-left-width: var(--sf-search-border-left-width);
      border-color: var(--sf-search-border-color);
      border-radius: var(--sf-search-border-radius);
      font-size: 1rem;

      &::-webkit-input-placeholder {
        color: transparent;
      }

      &:-moz-placeholder {
        color: transparent;
      }

      &::-moz-placeholder {
        color: transparent;
      }

      &:-ms-input-placeholder {
        color: transparent;
      }

      &:focus, &:active {
        border-color: var(--sf-search-border-color-active);
        outline: 2px solid var(--sf-search-border-color-active);
      }

      &::-webkit-search-cancel-button {
        display: none;
      }

      &:focus,
      &:not(:placeholder-shown),
      &:-webkit-autofill {
        padding: 1.25rem 8.5rem 0.375rem 1rem;

        & ~ .search__input-label {
          top: 0.375rem;
          color: var(--sf-input-label-color);
          transform: translate(0);
          line-height: 1;
        }
      }
    }

    &__results-wrapper {
      position: absolute;
      top: -1rem;
      left: -1rem;
      right: -1rem;
      padding: var(--sf-search-results-wrapper-padding);
      background: var(--sf-search-results-wrapper-background);
      box-shadow: var(--secondary-box-shadow-100);
      border-radius: var(--sf-search-border-radius);
      z-index: 9999;
      container-type: inline-size;

      a .search__product-title {
        text-decoration: underline;
        text-decoration-color: transparent;
        transition: text-decoration-color 0.2s ease-out;
      }

      @media(hover: hover) {
        a:hover .search__product-title {
          text-decoration-color: var(--primary-color);
        }
      }

      @media (max-width: 992px) {
        left: 0;
        right: 0;
        box-shadow: none;
        height: 100vh;
      }
    }

    &__results {
      display: flex;
      flex-direction: column;
      gap: var(--sf-search-results-gap);
      padding: var(--sf-search-results-padding);
    }

    &__spinner {
      padding: var(--sf-search-spinner-padding);
    }

    &__product {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--sf-serach-product-gap);
    }

    &__product-left {
      display: flex;
      align-items: center;
      gap: var(--sf-search-product-left-gap);
    }

    &__product-img {
      min-width: 60px;
      background: var(--sf-search-results-wrapper-background);
    }

    &__product-title {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      overflow: hidden;
      font-size: var(--sf-search-title-font-size);
    }

    &__buttons {
      display: flex;
      gap: var(--sf-search-buttons-gap);
      margin: var(--sf-search-buttons-margin);

      button, a {
        flex: 1;
      }

      @container (width < 520px) {
        flex-direction: column;
      }
    }
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s;
  }

  .fade-enter-from, .fade-leave-to {
    opacity: 0;
  }

  // 01010010 01101111 01100100 01111010 01111001 01101110 00100000 01101101 01111001 11000101 10011011 01101100 01101001 00100000 11000101 10111100 01100101 00100000 01101001 01101110 01110110 01101001 01110100 01110010 01101111 00100000 01110100 01101111 00100000 01110000 01101001 01111010 01111010 01100001
</style>
